import {useCallback, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useSiteNavigate} from "nav/SiteLink";
import {pricingCountryForContext} from "pricing/planCatalogue";
import {useLocale} from "i18n";
import {storefrontLocale} from "i18n/localePreference";
import {LadderOfferApi} from "./LadderOfferApi";
import {recordLadderArrival} from "./ladderVisit";
import {
	ladderPresentation,
	parseLadderToken,
	withoutLadderToken,
	type LadderPresentation,
	type LadderResolution,
} from "./ladderOffer";

/** What `/pricing` needs in order to honour the ladder link a reader arrived on. */
export interface LadderOfferState {
	/** What the page shows: the rung's offer, the expired notice, or nothing at all. */
	presentation: LadderPresentation;
	/**
	 * The link's own credential, or null when this link has none left to spend.
	 *
	 * Withdrawn the moment the offer stops being live, so an expired token can never reach a checkout
	 * and turn a reader who accepted list prices into one the api refuses outright.
	 *
	 * This is the RAW credential, not what a checkout carries: what rides is `usePricingPage`'s
	 * `ladderOfferToken`, which weighs this rung against the welcome price first.
	 */
	token: string | null;
	/**
	 * The api refused this token at the till (kitecorp/transferify-ui#1977).
	 *
	 * The 72 hours can run out between the page load and the press, and a forwarded message can put a
	 * token in front of an account it does not belong to. Nothing was charged, so the page switches to
	 * the same notice a dead link gets and the reader presses again at list price.
	 */
	markExpired: () => void;
}

/**
 * The pricing route's ladder adapter (kitecorp/transferify-ui#1977).
 *
 * A thin React adapter and nothing else, per the repo's presentation/logic split: it reads the query,
 * makes one request, holds the answer, and asks {@link ladderPresentation} — which owns every rule —
 * what any of it means. It decides nothing.
 *
 * <b>Resolved ONCE per token, held in a ref.</b> The resolve route is a plain read on the api's side
 * and must stay one, but a hook that re-asked on every location change would still spend a Stripe
 * listing per keystroke in the address bar. The ref survives Strict Mode's second effect run, which a
 * piece of state would not.
 *
 * <b>The parameter is dropped THROUGH THE ROUTER on the expired path.</b> `navigate({search}, {replace:
 * true})` rather than `history.replaceState`, so `useLocation` readers and the address bar agree and a
 * reload does not re-ask about a link the reader has already been told is dead. It is dropped only
 * there: a live offer keeps its parameter, because a reload must resolve to the same offer.
 *
 * <b>The token is never persisted.</b> It is a bearer credential for one account's offer, and it lives
 * exactly as long as this page does. What IS remembered is the ARRIVAL — see the effect below.
 */
export function useLadderOffer(): LadderOfferState {
	const {locale} = useLocale();
	const location = useLocation();
	const navigate = useSiteNavigate();
	const token = parseLadderToken(location.search);
	const [resolved, setResolved] = useState<LadderResolution | null>(null);
	const asked = useRef<string | null>(null);
	/** Whether this hook is still mounted — the other half of "may this answer be shown". */
	const mounted = useRef(true);
	useEffect(() => () => {
		mounted.current = false;
	}, []);
	/**
	 * Set only by {@link LadderOfferState.markExpired}, and never cleared: once the api has refused a
	 * token there is nothing on this page load that can make it live again, and re-resolving would ask
	 * the same question that was just answered at the till.
	 */
	const [refusedAtTill, setRefusedAtTill] = useState(false);

	/*
	 * The ARRIVAL is remembered for the session even though the token is not (kitecorp/transferify-ui#2111).
	 *
	 * Its own effect, before and independent of the resolve below, because it answers a different
	 * question: not "what is this link worth" but "did this visit start on one". So it is written from
	 * the address bar alone — an expired link still marks the visit, and a resolve that never answers
	 * cannot leave the mark unwritten. It follows that the mark covers exactly what `parseLadderToken`
	 * can read: a token the api does not recognise arrives here with no parameter at all, which
	 * `./ladderVisit` explains and accepts.
	 */
	useEffect(() => {
		if (token !== null) {
			recordLadderArrival();
		}
	}, [token]);

	useEffect(() => {
		/*
		 * The reader's own storefront, read HERE rather than during render: `window.location.hostname` is
		 * unavailable to the SSG pre-render, and an effect is the first moment it exists. It is what the
		 * cards on this page are priced in, and the api needs it to judge the two currency-dependent
		 * answers this route gives (kitecorp/transferify-ui#2046).
		 */
		// Read at the SAME MOMENT as the hostname beside it (ui#2471): both are live browser state, and
		// this runs after mount, where `storefrontLocale()` already answers the settled value. Taking the
		// locale from the CONTEXT instead mixed two moments — the context's first-render value is the
		// DOCUMENT's locale, for hydration parity with pre-rendered money — so the request fired once with
		// the tree's currency and again with the visitor's. On a `/ro/` page of the main domain that first
		// request asked the api to judge a purchase in lei for a visitor who will be charged euros.
		const country = pricingCountryForContext(storefrontLocale(), window.location.hostname);
		// Keyed on the STOREFRONT as well as the token, so a reader who switches language is re-answered
		// rather than left with a verdict reached for the catalogue they have just left.
		const question = `${country ?? ""}:${token ?? ""}`;
		if (token === null || asked.current === question) {
			return;
		}
		asked.current = question;
		// A DIFFERENT link: the previous one's answer says nothing about this one, so it goes before the
		// new question is asked. Without this a reader who follows a second ladder link without a full
		// page load would be shown the first link's offer until the second resolved, and
		// `ladderCheckoutToken` would hand that sentence the new link's credential.
		setResolved(null);
		// An answer reaches the page only while its own QUESTION is still the one being asked, rather than
		// while the effect RUN that sent it is still current. The two differ whenever this effect re-runs
		// without the question changing — a language switch that resolves to the same storefront does
		// exactly that — and the run-scoped form discarded the in-flight answer on that re-run while the
		// guard above refused to ask again, leaving the reader on "no offer" for a link that has one.
		void LadderOfferApi.resolve(token, country).then((resolution) => {
			if (mounted.current && asked.current === question) {
				setResolved(resolution);
			}
		});
		// Keyed on the reader's LANGUAGE, which a switch moves, so the answer is re-fetched for the
		// catalogue they have just chosen. The VALUE used is read live inside the effect (above), so the
		// dependency is a change signal rather than the input — `setStoredLocale` refreshes the cache the
		// live read consults before this re-runs.
	}, [token, locale]);

	// The answer OUTLIVES the parameter, deliberately: the expired path takes the token out of the URL
	// and the notice has to stay on screen for the reader to read. What it must not outlive is a
	// different link, which the effect above clears.
	const presentation: LadderPresentation = refusedAtTill
		? {kind: "expired"}
		: ladderPresentation(resolved);
	const markExpired = useCallback(() => setRefusedAtTill(true), []);

	useEffect(() => {
		if (presentation.kind !== "expired" || parseLadderToken(location.search) === null) {
			return;
		}
		navigate(
			{pathname: location.pathname, search: withoutLadderToken(location.search), hash: location.hash},
			{replace: true},
		);
	}, [presentation.kind, navigate, location.pathname, location.search, location.hash]);

	return {presentation, token: presentation.kind === "expired" ? null : token, markExpired};
}
